import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface TwoFactorFormProps {
  onSubmit: (otp: string) => void;
  phoneNumber: string;
  onResend: () => void;
  loading: boolean;
}

const TwoFactorForm: React.FC<TwoFactorFormProps> = ({
  onSubmit,
  onResend,
  loading
}) => {
  const { t } = useTranslation();
  const [otp, setOtp] = useState<string[]>(['', '', '', '']);
  const pinRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Function to handle changes in input fields
  const handleChange = (element: HTMLInputElement, index: number) => {
    const value = element.value;
    if (isNaN(Number(value))) return;

    const newOtp = [...otp];
    // Take the last character if user types more than 1 char (though maxLength is 1, paste might ignore it)
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Focus next input
    if (value && index < 3 && pinRefs.current[index + 1]) {
      pinRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        e.preventDefault();
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);

        const prevInput = pinRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
          requestAnimationFrame(() => {
            prevInput.setSelectionRange(0, 0);
          });
        }
      }
    } else if (e.key === 'ArrowLeft') {
      if (index > 0 && pinRefs.current[index - 1]) {
        e.preventDefault();
        const prevInput = pinRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
          requestAnimationFrame(() => {
            prevInput.setSelectionRange(prevInput.value.length, prevInput.value.length);
          });
        }
      }
    } else if (e.key === 'ArrowRight') {
      if (index < 3 && pinRefs.current[index + 1]) {
        e.preventDefault();
        const nextInput = pinRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          requestAnimationFrame(() => {
            nextInput.setSelectionRange(nextInput.value.length, nextInput.value.length);
          });
        }
      }
    }
  };

  const [timeLeft, setTimeLeft] = useState(60);

  useEffect(() => {
    if (timeLeft === 0) return;

    const intervalId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timeLeft]);

  const handleResendClick = () => {
    if (loading) return;
    onResend();
    setOtp(['', '', '', '']);
    setTimeLeft(60);
    pinRefs.current[0]?.focus();
  };

  const handleInternalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    onSubmit(otp.join(''));
  };

  return (
    <form id="twoStepsForm" onSubmit={handleInternalSubmit}>
      <div className="mb-3">
        <div className="auth-input-wrapper d-flex align-items-center justify-content-between numeral-mask-wrapper">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { pinRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control auth-input text-center numeral-mask h-px-50 mx-sm-1 my-2"
              maxLength={1}
              value={digit}
              autoFocus={index === 0}
              onChange={(e) => handleChange(e.target, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={(e) => {
                  e.preventDefault();
                  const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4).split('');
                  if (pastedData.length === 0) return;
                  
                  const newOtp = [...otp];
                  
                  pastedData.forEach((char, i) => {
                    if (index + i < 4) {
                      newOtp[index + i] = char;
                    }
                  });
                  setOtp(newOtp);
                  
                  // Focus the input after the last pasted char
                  const nextIndex = Math.min(index + pastedData.length, 3);
                  pinRefs.current[nextIndex]?.focus();
              }}
              disabled={loading}
            />
          ))}
        </div>
        <input type="hidden" name="otp" value={otp.join('')} />
      </div>
      <button 
        className="btn btn-primary d-grid w-100 mb-5" 
        type="submit" 
        disabled={loading}
      >
        {loading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : 'Verify my account'}
      </button>
      <div className="text-center">
        Didn't get the code?
        {timeLeft > 0 ? (
          <span className="ms-1 text-muted">Resend in {timeLeft}s</span>
        ) : (
          <a 
            href="#" 
            className={loading ? 'disabled text-muted' : ''}
            onClick={(e) => {
              e.preventDefault();
              handleResendClick();
            }}
            style={{ pointerEvents: loading ? 'none' : 'auto' }}
          > Resend </a>
        )}
      </div>
    </form>
  );
};

export default TwoFactorForm;
